import { Box } from "@mui/system";
import {
  Button,
  IconButton,
  Skeleton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import PageTitle from "../PageTitle";
import { useContext, useEffect, useState } from "react";
import EnhancedTable from "../../layouts/table";
import FilterForm from "./FilterForm";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import axios from "axios";
import LoadingTableRows from "../LoadingTableRows";
import { useOutletContext } from "react-router-dom";
import { AppContext } from "../../scripts/context/App";

export interface IFilter {
  status: boolean;
  data: {
    name: string;
    wallet_address: string;
  };
}
export const initFilterData: IFilter = {
  status: true,
  data: {
    name: "",
    wallet_address: "",
  },
};

export default function SponsorManagement() {
  const { user } = useContext(AppContext);
  const { updateClaimers }: any = useOutletContext();
  const [tableData, setTableDatas] = useState({
    page: 1,
    pagesCount: -1,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    tableHead: [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Full Name",
      },
      {
        id: "affiliate_number",
        numeric: false,
        disablePadding: false,
        label: "Affiliate No.",
      },
      {
        id: "total_bullit",
        numeric: false,
        disablePadding: false,
        label: "Total BULT",
      },
      {
        id: "commission_rate",
        numeric: false,
        disablePadding: false,
        label: "Commission Rate",
      },
      {
        id: "commission_amount",
        numeric: false,
        disablePadding: false,
        label: "Commission Amount",
      },
    ],
    tableItems: [],
  });
  const setTableData = (data: any) => setTableDatas({ ...tableData, ...data });

  const [filter, setFilter] = useState<IFilter>(initFilterData);
  const [statistics, setStatistics] = useState<any>({
    claimerLatestUpdate: "",
    sponsorTotalCommission: "",
    totalPurchasedByAffiliates: "",
  });
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState({
    order: "asc",
    orderBy: "name",
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTableData({
      rowsPerPage: Number(event.target.value),
      page: 1,
    });

    getSponsorList({
      name: filter.data.name,
      wallet_address: filter.data.wallet_address,
      sortBy: order.order,
      sortOrder: order.orderBy,
      page: 1,
      perPage: event.target.value,
    });
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    getSponsorList({
      name: filter.data.name,
      wallet_address: filter.data.wallet_address,
      sortBy: order.order,
      sortOrder: order.orderBy,
      page: newPage,
      perPage: tableData.rowsPerPage,
    });

    setTableData({ page: newPage });
  };

  const getSponsorList = async (params?: any) => {
    const {
      data: { data },
    } = await axios.get("/sponsor-management/get-sponsors", { params });

    setTableData({
      tableItems: data.data,
      pagesCount: data.total,
      rowsPerPage: data.per_page,
      page: data.current_page,
    });
  };

  const getSponsorsStatistics = async () => {
    const {
      data: { data },
    } = await axios.get("/sponsor-management/get-sponsors-statistics");

    setStatistics(data);
  };

  const getInitData = async () => {
    if (!user.permissions.includes("view_sponsors")) return;

    await getSponsorList();
    await getSponsorsStatistics();

    setLoading(false);
  };

  useEffect(() => {
    getInitData();
  }, []);

  if (!user.permissions.includes("view_sponsors")) return (
    <Box>
      <h1>You dont have permissions to view this page</h1>
    </Box>
  )

  return (
    <>
      <PageTitle
        title="Sponsor Management"
        description="Manage your Sponsor"
        leftElement={
          loading ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" color="textPrimary">
                Last time updated:{" "}
              </Typography>
              <Skeleton sx={{ minWidth: "70px", ml: 1 }} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="body2" color="textPrimary">
                Last time updated:{" "}
                <span style={{ fontWeight: "bold" }}>
                  {statistics.claimerLatestUpdate}
                </span>
              </Typography>
              <Button variant="outlined" onClick={updateClaimers} disabled={!user.permissions.includes("update_claimers")}>Update claimers</Button>
            </Box>
          )
        }
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {[
            {
              label: "Affiliates' Total BULT Purchased",
              key: "totalPurchasedByAffiliates",
            },
            {
              label: "Sponsors' Total Commissions",
              key: "sponsorTotalCommission",
            },
          ].map((claimer: any, index: number) => (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                flex: "1 1 290px",
                border: "1px solid rgba(72, 94, 144, 0.16)",
                p: 2,
              }}
              key={index}
            >
              <Typography variant="subtitle2" color="textPrimary">
                {claimer.label}
              </Typography>
              {loading ? (
                <Skeleton />
              ) : (
                <Typography variant="h6">{statistics[claimer.key]}</Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ p: 3 }}>
        <EnhancedTable
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          handleRequestSortHeader={(orderFunc, orderByFunc) => {
            if (!orderByFunc) return;

            getSponsorList({
              name: filter.data.name,
              wallet_address: filter.data.wallet_address,
              sortBy: orderByFunc,
              sortOrder: orderFunc,
              page: 1,
              perPage: tableData.rowsPerPage,
            });
            setOrder({
              order: orderByFunc,
              orderBy: orderFunc,
            });
          }}
          TableRows={
            loading ? (
              <LoadingTableRows elementsCount={tableData.tableHead.length} />
            ) : tableData.tableItems.length > 0 ? (
              tableData.tableItems.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.affiliate_number}</TableCell>
                  <TableCell>{row.total_bullit}</TableCell>
                  <TableCell>{row.commission_rate}</TableCell>
                  <TableCell>{row.commission_amount}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableData.tableHead.length}>
                  <Typography variant="body2" color="textPrimary">
                    No data found
                  </Typography>
                </TableCell>
              </TableRow>
            )
          }
          {...tableData}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() =>
                  setFilter({
                    ...filter,
                    status: !filter.status,
                  })
                }
              >
                {filter.status ? <FilterListIcon /> : <FilterListOffIcon />}
              </IconButton>
              <Typography variant="h6">Filter by</Typography>
            </Box>
            {filter.status && (
              <FilterForm
                filter={filter}
                setFilter={setFilter}
                handleSearch={(data) => {
                  if (data?.data) {
                    getSponsorList({
                      name: data.data.name,
                      wallet_address: data.data.wallet_address,
                      page: 1,
                      perPage: tableData.rowsPerPage,
                    });
                  } else {
                    getSponsorList({
                      name: filter.data.name,
                      wallet_address: filter.data.wallet_address,
                      page: 1,
                      perPage: tableData.rowsPerPage,
                    });
                  }
                }}
              />
            )}
          </Box>
        </EnhancedTable>
      </Box>
    </>
  );
}
